import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2 } from "lucide-react";

const SUGGESTED = [
  "Riassumi lo stato del progetto e le attività in corso",
  "Quali sono le criticità o i punti aperti?",
  "Suggerisci una checklist delle prossime scadenze",
  "Genera una bozza di email al cliente",
];

export default function ProjectAISidebar({ projectId }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Sono l'assistente AI di questo progetto. Posso aiutarti riassumendo lo stato, suggerendo prossime azioni, generando bozze email o checklist. Ogni risposta è basata sui dati del progetto." }
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);
  useEffect(() => { setMessages(m => m.slice(0,1)); setSessionId(null); }, [projectId]);

  const send = async (msg) => {
    const txt = msg || input;
    if (!txt.trim() || busy) return;
    setMessages(m => [...m, { role: "user", content: txt }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: txt, project_id: projectId, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: "assistant", content: data.reply }]);
    } catch { setMessages(m => [...m, { role: "assistant", content: "Errore: impossibile contattare l'assistente." }]); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-[600px] bg-white rounded-md border border-slate-200" data-testid="project-ai-sidebar">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
        <div>
          <div className="font-heading font-semibold text-navy-900 text-sm">Assistente AI · Progetto</div>
          <div className="text-[10px] text-slate-500">Risponde solo sui dati di questa pratica</div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] rounded-lg px-3 py-2 text-sm whitespace-pre-line ${m.role === "user" ? "bg-navy-800 text-white" : "bg-slate-100 text-slate-800"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-500 flex items-center gap-2 self-start"><Loader2 className="w-3 h-3 animate-spin" /> Sto pensando...</div>}
        <div ref={endRef} />
      </div>
      {messages.length <= 1 && (
        <div className="px-3 pb-2 grid gap-1.5">
          {SUGGESTED.map((s, i) => (
            <button key={i} onClick={() => send(s)} className="text-left text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors text-slate-700">
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="border-t border-slate-100 p-3 flex gap-2">
        <Input value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&send()} placeholder="Chiedi sul progetto..." disabled={busy} data-testid="ai-input" />
        <Button onClick={()=>send()} disabled={busy || !input.trim()} className="bg-navy-800 hover:bg-navy-900" size="icon" data-testid="ai-send"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
